import { createWebHistory, createRouter } from "vue-router";
import pinia, { useAuthStore } from "@/state/pinia";
import { useSiteStore } from "@/state/pinia/site";
import routes from "./routes";

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) return savedPosition;
    return { x: 0, y: 0 };
  },
});

const PUBLIC_PREFIXES = ["/login", "/logout", "/auth/callback"];

// Ścieżki na których onboarding guard NIE działa — tu user może wylądować
// gdy nie ma jeszcze workspace/project (sam onboarding flow).
const ONBOARDING_PATHS = [
  "/workspaces/new",
  "/workspaces",
  "/sites",
];

function isPublic(path) {
  return PUBLIC_PREFIXES.some((p) => path.startsWith(p));
}

function isOnboarding(path) {
  return ONBOARDING_PATHS.some((p) => path === p || path.startsWith(p + "/"));
}

// Strony workspace-/konto-level — działają bez wybranej witryny, więc guard
// „brak witryny → lista witryn" ich NIE przekierowuje (np. Integracje, w których
// łączysz Google zanim w ogóle dodasz witrynę).
const SITE_OPTIONAL_PREFIXES = ["/settings", "/workspaces", "/billing", "/users", "/user"];

function siteOptional(path) {
  return SITE_OPTIONAL_PREFIXES.some((p) => path === p || path.startsWith(p + "/"));
}

router.beforeEach(async (to, from, next) => {
  const auth = useAuthStore(pinia);

  if (!auth.token && auth.loadToken) {
    auth.loadToken();
  }

  const token = auth.getToken && auth.getToken();
  const publicRoute = isPublic(to.path);
  const ssoOn = auth.isSsoEnabled && auth.isSsoEnabled();

  // SSO mode: NIGDY nie pokazuj lokalnego formularza logowania. Wejście na /login
  // bez tokena → od razu redirect na SSO authorize. (/auth/callback zostaje publiczny,
  // żeby dokończyć PKCE.) Lokalny /login działa tylko gdy SSO nie jest skonfigurowane.
  if (ssoOn && !token && to.path.startsWith("/login")) {
    auth.redirectToSso();
    return;
  }

  // 1. Bez tokena → SSO (jeśli skonfigurowane), inaczej lokalny login
  if (!publicRoute && !token) {
    if (ssoOn) {
      auth.redirectToSso();
      return;
    }
    return next({ name: "login" });
  }

  // 2. Zalogowany na /login → wyrzuć na dashboard
  if (to.name === "login" && token) {
    return next({ name: "default" });
  }

  // 3. Onboarding guard — wymaga workspace, potem project.
  //    Pomijamy gdy user jest już na ścieżce onboardingu lub logout.
  if (token && !publicRoute && !isOnboarding(to.path) && to.path !== "/logout") {
    let user = auth.getUser?.() || auth.user;
    let hasWorkspace = Object.keys(user?.workspaces || {}).length > 0;

    // Cached user may be stale (e.g. workspace added in SSO after login, or a
    // session created before workspace-sync existed). Before forcing onboarding,
    // re-fetch /me which live-syncs workspaces from SSO, then re-check.
    if (!hasWorkspace) {
      await auth.refreshMe();
      user = auth.getUser?.() || auth.user;
      hasWorkspace = Object.keys(user?.workspaces || {}).length > 0;
    }

    if (!hasWorkspace) {
      return next({ name: "workspaces.create" });
    }

    let hasSite = Object.keys(user?.sites || {}).length > 0;

    // Cache może być nieaktualny (witryna dodana/przypisana po loginie, albo
    // payload z wcześniejszej, częściowej sesji). Zanim wyrzucimy na listę
    // witryn — odśwież /me i sprawdź ponownie.
    if (!hasSite) {
      await auth.refreshMe();
      user = auth.getUser?.() || auth.user;
      hasSite = Object.keys(user?.sites || {}).length > 0;
    }

    // Brak witryny → lista witryn (tam CTA „Dodaj witrynę"). Pomijamy strony
    // workspace-level (Integracje/Ustawienia/Billing), które witryny nie wymagają.
    if (!hasSite && !siteOptional(to.path)) {
      return next({ name: "sites.index" });
    }

    const sites = user?.sites || {};
    const siteStore = useSiteStore(pinia);

    // Wejście do systemu („/”) → dashboard witryny, w której user był ostatnio
    // (jak w LessCMS). Ostatnia mogła zniknąć z dostępów (inne konto, odebrane
    // uprawnienia) — wtedy pierwsza dostępna.
    if (to.name === "default" && hasSite) {
      const last = localStorage.getItem(siteStore.storageKey());
      const uuid = last && sites[last] ? last : Object.keys(sites)[0];
      return next({ name: "sites.show", params: { uuid } });
    }

    // Każde wejście w witrynę (link, lista, odświeżenie) zapamiętuje ją jako
    // ostatnią — nie tylko przełącznik w topbarze.
    const siteUuid = to.params?.uuid;
    if (typeof siteUuid === "string" && to.path.startsWith("/sites/") && sites[siteUuid] && siteStore.activeSite !== siteUuid) {
      if (!siteStore.sites.length) siteStore.loadFromAuth();
      siteStore.setActive(siteUuid);
    }
  }

  next();
});

router.afterEach((to) => {
  document.title = `${to.meta.title || "LessSEO"} | LessSEO`;
});

export default router;
